import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { COLORS, SHADOWS, BORDER_RADIUS } from '../../styles/UISystem';
import { ALL_CONTACT_TYPES } from '../../utils/contactTypeConfig';
import { getBaseType } from '../../utils/contactKeys';
import { formatPhoneNumber } from '../../utils/formatters';

/**
 * ShareTray
 *
 * The chat composer's share entry (social-track #14, mocked in-chat at refine):
 * a single-action list — one row per FILLED contact value (two phones = two
 * rows), one tap = share that one thing → its speech line in the room (the
 * caller posts it). No checkboxes, no submit, no Name/About/Hometown — those
 * live in the Detail page's selection list. On connection, speed: connect,
 * state name, pick one.
 *
 * User-initiated → standard scrim + tap-outside dismiss (§4.2), unlike the
 * system-raised ShareSheet.
 *
 * @param {boolean} show
 * @param {Array<{fieldKey: string, value: string}>} rows - filled contact
 *   values in registry order (fieldKey may be a secondary variant, e.g. phone2)
 * @param {function} onShareValue - (fieldKey, value) one-tap share
 * @param {function} onClose - scrim tap dismiss
 */
const ShareTray = ({ show, rows = [], onShareValue, onClose }) => {
  const displayValue = (fieldKey, value) => {
    const base = getBaseType(fieldKey);
    if (base === 'phone' || base === 'whatsapp') return formatPhoneNumber(value);
    return value;
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Scrim — user-initiated surface (§4.2) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 999,
            }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } }}
            exit={{ y: '100%', transition: { duration: 0.3, ease: 'easeIn' } }}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: '#FFFFFF',
              borderRadius: '24px 24px 0 0',
              boxShadow: SHADOWS.TIER3,
              padding: '28px 20px calc(32px + env(safe-area-inset-bottom))',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '14px',
            }}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: COLORS.CHAT_TEXT_DARK,
                textAlign: 'center',
                marginBottom: '10px',
              }}
            >
              Share
            </div>

            {rows.length === 0 && (
              <div style={{ fontSize: '15px', color: '#8E8E93', padding: '8px 0 4px' }}>
                You&apos;ve shared everything
              </div>
            )}
            {rows.map(({ fieldKey, value }) => {
              const config = ALL_CONTACT_TYPES[getBaseType(fieldKey)];
              if (!config) return null;
              const Icon = config.Icon;
              return (
                <button
                  key={fieldKey}
                  aria-label={`Share ${config.label}`}
                  onClick={() => onShareValue?.(fieldKey, value)}
                  style={{
                    width: '100%',
                    maxWidth: '420px',
                    minHeight: '52px',
                    borderRadius: BORDER_RADIUS.PILL,
                    border: '1px solid #E0E0E0',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '0 20px',
                    fontSize: '17px',
                    fontWeight: 400,
                    color: '#8E8E93',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <Icon size={22} color="#8E8E93" weight="regular" />
                  {displayValue(fieldKey, value)}
                </button>
              );
            })}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

ShareTray.propTypes = {
  show: PropTypes.bool,
  rows: PropTypes.arrayOf(PropTypes.shape({ fieldKey: PropTypes.string, value: PropTypes.string })),
  onShareValue: PropTypes.func,
  onClose: PropTypes.func,
};

export default ShareTray;
